import { useMagicLinkSignIn } from "@serophin/browser";
import { useEffect, useState } from "preact/hooks";
import { finishSignIn } from "../../components/auth/signInConfig";
import type { PageProps } from "../pageProps";

const APP_REDIRECT_FALLBACK_MS = 1500;

const isMobileUserAgent = (): boolean => /iphone|ipad|android/i.test(navigator.userAgent);

const Spinner = () => <div class="inline-block w-8 h-8 border-2 border-neutral-300 dark:border-neutral-600 border-t-blue-500 rounded-full animate-spin mb-4" />;

const Message = ({ children }: { children: string }) => (
  <div class="flex justify-center items-center min-h-[60vh] text-center">
    <div>
      <Spinner />
      <p class="text-neutral-600 dark:text-neutral-400">{children}</p>
    </div>
  </div>
);

/** Verifies the token in the link; a bad or expired link goes back to /auth with a message. */
const WebVerifier = () => {
  const { status } = useMagicLinkSignIn({ onSignedIn: (result) => finishSignIn("magic-link", result) });
  useEffect(() => {
    if (status === "failed") {
      window.location.replace("/auth?error=invalid_magic_link");
    }
  }, [status]);
  return <Message>Signing you in...</Message>;
};

/** Tries `nottldr://auth/verify`, and carries on in the browser when the app does not take the link. */
const AppRedirect = ({ token }: { token: string }) => {
  const fallback = `/auth/verify?token=${encodeURIComponent(token)}&skip_app_redirect=true`;
  useEffect(() => {
    window.location.href = `nottldr://auth/verify?token=${encodeURIComponent(token)}`;
    const timer = setTimeout(() => window.location.replace(fallback), APP_REDIRECT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [token, fallback]);
  return (
    <div class="flex justify-center items-center min-h-[60vh] text-center">
      <div>
        <Spinner />
        <p class="text-neutral-900 dark:text-neutral-100">Opening !tldr app...</p>
        <p class="text-sm text-neutral-500 mt-5">
          Not working?{" "}
          <a href={fallback} class="text-blue-500">
            Continue in browser
          </a>
        </p>
      </div>
    </div>
  );
};

type Mode = { kind: "checking" } | { kind: "app"; token: string } | { kind: "web" };

/**
 * /auth/verify?token= - the magic link in the sign-in email. On iOS and Android it first tries the app's scheme
 * (`nottldr://auth/verify?token=`) and, after 1.5 s, continues here with `skip_app_redirect=true`; elsewhere it signs in
 * on the web and goes to `?next=` or `/`.
 */
export const AuthVerifyPage = (_props: PageProps) => {
  const [mode, setMode] = useState<Mode>({ kind: "checking" });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const skip = params.get("skip_app_redirect") === "true";
    setMode(token && !skip && isMobileUserAgent() ? { kind: "app", token } : { kind: "web" });
  }, []);

  if (mode.kind === "app") {
    return <AppRedirect token={mode.token} />;
  }
  return mode.kind === "web" ? <WebVerifier /> : <Message>Signing you in...</Message>;
};
